import {
  DISCIPLINES,
  LEVEL_PATTERN,
  UNIT_PATTERN,
  isDiscipline,
  type Discipline,
} from './naming.ts';

/**
 * View state, encoded in the URL (§1).
 *
 *   `?unit=VL01&dwelling=VL01-A&level=L01&layer=mech&light=night`
 *
 * Every visual state is addressable, shareable on WhatsApp and a back-button
 * stop. This module is the only place that knows the query-string spelling —
 * the app reads and writes `ViewState`, never `URLSearchParams`.
 */

export type LightingState = 'day' | 'night';

export interface ViewState {
  /** null = masterplan. */
  unit: string | null;
  /** null = the unit as a whole; otherwise a dwelling of that unit. */
  dwelling: string | null;
  /** null = every level of the current selection. */
  level: string | null;
  /** Empty = architecture only. Order is not significant. */
  layers: Discipline[];
  light: LightingState;
}

export const DEFAULT_VIEW_STATE: Readonly<ViewState> = Object.freeze({
  unit: null,
  dwelling: null,
  level: null,
  layers: ['arch'] as Discipline[],
  light: 'day' as LightingState,
});

const KEYS = {
  unit: 'unit',
  dwelling: 'dwelling',
  level: 'level',
  layer: 'layer',
  light: 'light',
} as const;

const DWELLING_PATTERN = /^[A-Z][A-Z0-9]{1,7}-[A-Z0-9]{1,4}$/;

/**
 * Decode. Unknown or malformed values fall back to the default rather than
 * throwing — a URL forwarded through three chat apps must still open.
 */
export function parseViewState(search: string | URLSearchParams): ViewState {
  const params =
    typeof search === 'string' ? new URLSearchParams(search) : search;

  const unitRaw = (params.get(KEYS.unit) ?? '').toUpperCase();
  const unit = UNIT_PATTERN.test(unitRaw) ? unitRaw : null;

  const dwellingRaw = (params.get(KEYS.dwelling) ?? '').toUpperCase();
  const dwelling =
    unit !== null &&
    DWELLING_PATTERN.test(dwellingRaw) &&
    dwellingRaw.startsWith(`${unit}-`)
      ? dwellingRaw
      : null;

  const levelRaw = (params.get(KEYS.level) ?? '').toUpperCase();
  const level = unit !== null && LEVEL_PATTERN.test(levelRaw) ? levelRaw : null;

  const layerRaw = params.getAll(KEYS.layer).flatMap((v) => v.split(','));
  const layers = dedupe(
    layerRaw.map((v) => v.trim().toLowerCase()).filter(isDiscipline),
  );

  const lightRaw = params.get(KEYS.light);
  const light: LightingState = lightRaw === 'night' ? 'night' : 'day';

  return {
    unit,
    dwelling,
    level,
    layers: layers.length > 0 ? layers : [...DEFAULT_VIEW_STATE.layers],
    light,
  };
}

/**
 * Encode. Defaults are omitted so the common URL stays short and a shared link
 * does not carry noise the recipient has to trust.
 */
export function formatViewState(state: ViewState): string {
  const params = new URLSearchParams();
  if (state.unit) params.set(KEYS.unit, state.unit);
  if (state.unit && state.dwelling) params.set(KEYS.dwelling, state.dwelling);
  if (state.unit && state.level) params.set(KEYS.level, state.level);

  const layers = orderLayers(state.layers);
  const isDefaultLayers =
    layers.length === 1 && layers[0] === DEFAULT_VIEW_STATE.layers[0];
  if (!isDefaultLayers && layers.length > 0) {
    params.set(KEYS.layer, layers.join(','));
  }
  if (state.light !== DEFAULT_VIEW_STATE.light) {
    params.set(KEYS.light, state.light);
  }

  const query = params.toString();
  return query ? `?${query}` : '';
}

export function viewStatesEqual(a: ViewState, b: ViewState): boolean {
  return formatViewState(a) === formatViewState(b);
}

/** Canonical layer order, so two equivalent states encode identically. */
export function orderLayers(layers: readonly Discipline[]): Discipline[] {
  const present = new Set(layers);
  return DISCIPLINES.filter((d) => present.has(d));
}

function dedupe(values: Discipline[]): Discipline[] {
  return orderLayers(values);
}

/** Analytics event name for a state transition. Kept beside the encoding. */
export function describeTransition(from: ViewState, to: ViewState): string {
  if (from.unit !== to.unit) return to.unit ? 'unit_open' : 'masterplan_open';
  if (from.dwelling !== to.dwelling) return 'dwelling_change';
  if (from.level !== to.level) return 'level_change';
  if (from.light !== to.light) return 'lighting_change';
  if (formatViewState(from) !== formatViewState(to)) return 'layer_change';
  return 'noop';
}
